// @ts-nocheck
import ErrorMessage from '@/components/form-control/ErrorMessage'
import InputLabelField from '@/components/form-control/InputLabelField'
import { CONFIG_NAME_MAX_LENGTH } from '@/constant/common'
import { CharacterPlusIcon } from '@/components/icons'
import DeleteIcon from '@/components/icons/DeleteIcon'
import { useAuthContext } from '@/features/auth/context'
import { useConfigUI } from '@/features/config/context'
import useAddArea from '@/features/config/hooks/areasandzones/useAddArea'
import useSearchSubAreaId from '@/features/config/hooks/areasandzones/useSearchSubAreaId'
import useUpdateArea from '@/features/config/hooks/areasandzones/useUpdateArea'
import { UpdateArea } from '@/types/areaZone'
import { yupResolver } from '@hookform/resolvers/yup'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  CircularProgress,
  Container,
  Fade,
  Stack,
  Typography,
} from '@mui/material'
import { useEffect, useRef } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import * as yup from 'yup'
import classes from './style.module.scss'
import useTranslation from '@/hooks/useTranslation'

interface FormAreaProps {
  id?: number
}


const FormAreaView = ({ id }: FormAreaProps) => {
  const { t } = useTranslation()
  const { openModal, setModalView, closeSidebar } = useConfigUI()
  const { mutate: addArea, isLoading: loadingAddArea } = useAddArea()
  const schema = yup.object({
    name: yup
      .string()
      .trim()
      .required(t('formsValidationAreaNameIsRequired'))
      .max(CONFIG_NAME_MAX_LENGTH, `Maximum ${CONFIG_NAME_MAX_LENGTH} characters`),
    subAreas: yup.array(
      yup.object({
        name: yup
          .string()
          .trim()
          .required(t('formsValidationSubAreaNameIsRequired'))
          .max(CONFIG_NAME_MAX_LENGTH, `Maximum ${CONFIG_NAME_MAX_LENGTH} characters`),
      }),
    ),
  })

  const inputRefSiteName = useRef<HTMLInputElement>(null)
  const { interfaceId } = useAuthContext()
  const { data, isLoading } = useSearchSubAreaId(interfaceId, id)
  const { mutate: updateArea } = useUpdateArea()
  const form = useForm<UpdateArea>({
    resolver: yupResolver(schema),
  })

  const {
    formState: { errors },
  } = form

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'subAreas',
  })
  useEffect(() => {
    if (inputRefSiteName.current) {
      inputRefSiteName.current.focus()
    }
  }, [])

  useEffect(() => {
    if (data) {
      form.reset({
        name: data.name,
        subAreas: data.subAreas?.map((item) => ({
          id: item.id,
          name: item.name,
        })),
      })
    } else {
      form.reset({
        name: '',
        subAreas: [{ name: '' }],
      })
    }
    // eslint-disable-next-line
  }, [data])

  const handleDelete = (index: number, id: number) => {
    if (fields.length === 1) {
      toast.error('There needs to be at least 1 sub-area per area')
    } else {
      openModal()
      setModalView({
        type: 'DELETE_AREA_VIEW',
        id: id.toString(),
        interfaceId: interfaceId,
        mode: 'Sub Area',
        name: form.getValues(`subAreas.${index}.name`),
        onSuccess: () => {
          remove(index)
        },
      })
    }

    return
  }

  const handleSave = (data: UpdateArea) => {
    if (id) {
      updateArea({
        id: id,
        interfaceId: Number(interfaceId),
        parentId: data.parentId,
        name: data.name,
        subAreas: data.subAreas?.map((item: any) => ({
          id: Number(item.id) || 0,
          name: item.name,
        })),
      })
    } else {
      addArea({
        interfaceId: Number(interfaceId),
        name: data.name,
        subAreas: data.subAreas?.map((item: any) => item.name),
      })
    }
  }
  return (
    <Container sx={{ maxWidth: '450px', height: '100vh' }}>
      {isLoading && (
        <Stack
          width="377px"
          height="100%"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress />
        </Stack>
      )}
      {!isLoading && (
        <Fade in={!isLoading}>
          <form onSubmit={form.handleSubmit(handleSave)}>
            <Stack sx={{ padding: '34px 18px' }}>
              <Stack className={classes.header}>
                <Typography>{id ? `${t('commonActionsEdit')} ${t('Areas&SubAreasArea')}` : `${t('commonActionsAdd')} ${t('Areas&SubAreasArea')}`}</Typography>
                <Stack direction="row">
                  <Button
                    variant="secondary"
                    sx={{ marginRight: '7px', width: '85px' }}
                    onClick={() => closeSidebar()}
                  >
                    {t('commonActionsCancel')}
                  </Button>
                  <LoadingButton
                    size="small"
                    loading={loadingAddArea}
                    variant="primary"
                    sx={{ minWidth: '72px' }}
                    type="submit"
                  >
                    {t('commonActionsSave')}
                  </LoadingButton>
                </Stack>
              </Stack>

              <InputLabelField
                isRequired
                name="name"
                form={form}
                maxLength={CONFIG_NAME_MAX_LENGTH}
                label={t('AreaName')}
                placeholder={t('Areas&SubAreasArea')}
                inputRef={inputRefSiteName}
                style={{
                  marginTop: '8px',
                  borderRadius: '4px',
                  width: { xs: '377px' },
                }}
                padding="8px 16px"
              />

              <Stack className={classes.borderBottom}></Stack>

              <Stack className={classes.headerSub}>
                <Typography variant="subtitle2">{t('subareaList')}</Typography>
                <Stack className={classes.subHeadTitle}>
                  <Button
                    onClick={() => append({ id: 0, name: '' })}
                    sx={{
                      width: '10px',
                      minWidth: '10px !important',
                      backgroundColor: 'transparent',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        color: 'text.main',
                      },
                      color: 'text.main',
                    }}
                  >
                    <CharacterPlusIcon className={classes.addIcon} />
                  </Button>
                  <Typography
                    className={classes.addSubTitle}
                    onClick={() => append({ id: 0, name: '' })}
                  >
                    {t('commonActionsAdd')} {t('formsLabelsSubArea')}
                  </Typography>
                </Stack>
              </Stack>

              {fields.map((item, index) => {
                return (
                  <>
                    <Stack
                      direction={'row'}
                      key={item.id}
                      className={classes.subAreaWrap}
                    >
                      <InputLabelField
                        name={`subAreas.${index}.name`}
                        form={form}
                        maxLength={CONFIG_NAME_MAX_LENGTH}
                        label={t('SubAreaName')}
                        placeholder={t('subArea')}
                        style={{
                          marginTop: '8px',
                          borderRadius: '4px',
                          width: { xs: '377px' },
                        }}
                        padding="8px 16px"
                      />
                      <DeleteIcon
                        className={classes.deleteIcon}
                        onClick={() =>
                          handleDelete(
                            index,
                            form.getValues(`subAreas.${index}.id`),
                          )
                        }
                      />
                    </Stack>
                    <Stack maxWidth="340px">
                      <ErrorMessage
                        sx={{ mt: '5px' }}
                        message={errors.subAreas?.[index]?.name?.message}
                      />
                    </Stack>
                  </>
                )
              })}
            </Stack>
          </form>
        </Fade>
      )}
    </Container>
  )
}

export default FormAreaView
